import { QueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { Proof, ProofUploadRequest } from '@feature/proof/ProofTypes';
import { Id, getData, getQueryKey } from '@feature/utils';

export interface ProofApi {
  getProofs: (entityId: Id) => Promise<Proof[]>;
  getProofsByOwnerId: (entityId: Id, userId: Id) => Promise<Proof[]>;
  uploadOrUpdateProof: (entityId: Id, request: ProofUploadRequest) => Promise<Proof>;
  deleteProof: (entityId: Id, ownerId: Id) => Promise<unknown>;
}

export function generateProofApi(baseUrl: string): ProofApi {
  return {
    getProofs: (entityId: Id) => axios.get<Proof[]>(`${baseUrl}/${entityId}`).then(getData),
    getProofsByOwnerId: (entityId: Id, userId: Id) =>
      axios.get<Proof[]>(`${baseUrl}/${entityId}/owner/${userId}`).then(getData),
    uploadOrUpdateProof: (entityId: Id, request: ProofUploadRequest) => {
      const formData = new FormData();
      formData.append('file', request.file);
      formData.append('userId', request.userId.toString());

      return axios
        .post<Proof>(`${baseUrl}/${entityId}/owner/${request.userId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(getData);
    },
    deleteProof: (entityId: Id, ownerId: Id) =>
      axios.delete<unknown>(`${baseUrl}/${entityId}/owner/${ownerId}`).then(getData),
  };
}

export function generateProofOptions(keyRoot: string, api: ProofApi) {
  const proofKey = getQueryKey(keyRoot);

  return {
    getProofsOptions: (entityId: Id) => ({
      queryKey: proofKey.as([entityId, 'proofs']),
      queryFn: () => api.getProofs(entityId),
    }),
    getProofsByOwnerOptions: (entityId: Id, userId: Id) => ({
      queryKey: proofKey.as([entityId, 'proofs-by-owner', userId]),
      queryFn: () => api.getProofsByOwnerId(entityId, userId),
    }),
    createOrUpdateProofOptions: (entityId: Id, queryClient: QueryClient) => ({
      mutationFn: (request: ProofUploadRequest) => api.uploadOrUpdateProof(entityId, request),
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: proofKey.as([entityId, 'proofs']),
        }),
    }),
    deleteProofOptions: (entityId: Id, queryClient: QueryClient) => ({
      mutationFn: (ownerId: Id) => api.deleteProof(entityId, ownerId),
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: proofKey.as([entityId, 'proofs']),
        }),
    }),
  };
}
